import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { resetPassword, formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await resetPassword(token, pw); setDone(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Reset failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid="reset-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lock className="h-10 w-10 mx-auto text-atlas-blue mb-3" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Set a new password</h1>
        </div>
        {done ? (
          <div className="bg-card border border-border rounded-md p-8 text-center" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-4" />
            <p className="text-slate-700 mb-6">Your password has been updated.</p>
            <Button onClick={() => navigate("/login")} className="bg-primary text-white">Sign in</Button>
          </div>
        ) : !token ? (
          <p className="text-center text-red-600" data-testid="reset-no-token">This reset link is missing or invalid. Please request a new one from the <Link to="/forgot-password" className="text-atlas-blue">forgot password</Link> page.</p>
        ) : (
          <form onSubmit={submit} className="bg-card border border-border rounded-md p-6 md:p-8 space-y-5">
            <div className="space-y-1.5">
              <Label className="overline">New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters" minLength={8} required data-testid="reset-password-input" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-primary hover:bg-atlas-blue text-white py-6">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…</> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
